import { ComponentWithPermissions } from '../../../../../../../../baseComponents/CreateRoutering/types'
import { ModalMedium } from '../../../../../../../../baseComponents/Modal'
import { ModalCreateEditStructureLayout } from '../../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { PdfConfigTemplateTabProps } from '../types'
import useTwinTranslation from '../../../../../../../../utils/hooks/useTwinTranslation'
import { useCallback } from 'react'
import { permissionCheck } from '../../../../../../../../baseComponents/PermissionChecker/function'
import { FormRender } from '../../../../../../../../forms/FormRenderer/types'
import { PdfCssConfig } from '../../types'
import { valueOrDefaultValue } from '../../../../../../../../utils/globals/valueOrDefaultValue'
import FormRenderer from '../../../../../../../../forms/FormRenderer'
import TwinForm from '../../../../../../../../forms/TwinForm'
import { BeforeSubmitHandler } from '../../../../../../../../forms/TwinForm/types'
import useInputColorColors from '../../../../../../../../utils/hooks/pdf/useInputColorColors'
import {twinFetchPostText } from '../../../../../../../../utils/globals/data'
import { useParams } from 'react-router-dom'
import { addAntiUnsaveElement } from '../../../../../../../../utils/reducers/reduxDispatch'
import useTicketVariables from '../../../../../../../../utils/hooks/pdf/useTicketVariables'
import { TwinDictionary } from '../../../../../../../../utils/globals/dictionary'

type ModalConfigPdfEditDocumentStylesProps = ModalConfigPdfEditDocumentStylesLogicProps & ComponentWithPermissions & {}

const ModalConfigPdfEditDocumentStyles: React.FC<ModalConfigPdfEditDocumentStylesProps> = ({ userPermissions, setOpenModal, openModal, config, ...rest }) => {
    const {t} = useTwinTranslation()
    const { prevSubmitEditModule, getMyData } = useModalConfigPdfEditDocumentStylesLogic({ setOpenModal, openModal, config, ...rest })
    const { colors } = useInputColorColors()
    const key = openModal?.allRowData.id
    const allRowData = getMyData(key)
    const { fontSizes, fontWeights } = useTicketVariables()
    let sections: FormRender<PdfCssConfig> = []
    const specialTypes: TwinDictionary = {
        ticket: 'ticket',
        ticketRefund: 'ticket',
        voucher: 'ticket'
    }
    if (config.type && specialTypes[config.type] === 'ticket'){
        sections = [
            {
                cols: 3,
                className: 'mt-20',
                title: t('styles', 'Estilos'),
                elements: [
                    {
                        name: 'fontSizeTicket',
                        label: t('fontSize', 'Tamaño de letra'),
                        component: 'CustomSelect',
                        items: fontSizes
                    },
                    {
                        name: 'fontWeight',
                        label: t('fontWeight', 'Grosor letra'),
                        component: 'CustomSelect',
                        items: fontWeights
                    },
                ]
            },
        ]
    } else {
        sections = [
            {
                cols: 3,
                className: 'mt-20',
                title: t('styles', 'Estilos'),
                elements: [
                    {
                        name: 'fontSize',
                        label: t('fontSize', 'Tamaño de letra'),
                        id: 'fontSize',
                        component: 'InputWithLabelMargin',
                        type: 'text'
                    },
                    {
                        name: 'color',
                        label: t('letterColor', 'Color de letra'),
                        id: 'color',
                        component: 'InputColorDeleteButtonWithMargin',
                        colors: colors,
                    },
                    {
                        name: 'background',
                        id: 'background',
                        label: t('backgroundColor', 'Color de fondo'),
                        component: 'InputColorDeleteButtonWithMargin',
                        colors: colors,
                    },
                    {
                        name: 'padding',
                        id: 'padding',
                        label: t('padding', 'Margen interior'),
                        component: 'InputWithLabelMargin',
                        type: 'text'
                    },
                    {
                        name: 'orientation',
                        label: t('orientation', 'Orientación'),
                        component: 'CustomSelect',
                        items: {
                            portrait: {name: t('vertical', 'Vertical')},
                            landscape: {name: t('horizontal', 'Horizontal')}
                        }
                    },
                ]
            },
            {
                cols: 3,
                className: 'mt-20',
                title: t('background', 'Fondo'),
                elements: [
                    {
                        name: 'backgroundImage',
                        label: t('backgroundImage', 'Imagen de fondo'),
                        component: 'ImageInput',
                        defaultValue: allRowData?.backgroundImageParsed
                    }
                ]
            }
        ]
    }

      
    const parsedFields = valueOrDefaultValue(sections, userPermissions, allRowData)
    return (
        <ModalMedium setOpened={() => setOpenModal(null)} opened={true} className='flex flex-col'>
            <TwinForm beforeSubmitHandler={prevSubmitEditModule} className='flex flex-auto flex-col notFlexAutoFormRender' imagepath={['pdfTemplate', String(config?.id)]}>
                <ModalCreateEditStructureLayout translations={{ button: t('save', 'Guardar'), title: t('configDocument', 'Configurar documento') }} loading={false} haveButtonPermissions={permissionCheck(userPermissions, 'update')} className='modal_negative_margin flex flex-col'>
                    <div className='overflow-auto flex flex-auto flex-col'>
                        <FormRenderer sections={parsedFields} />
                    </div>
                </ModalCreateEditStructureLayout>
            </TwinForm>
        </ModalMedium>
    )
}

interface ModalConfigPdfEditDocumentStylesLogicProps extends PdfConfigTemplateTabProps {}

const useModalConfigPdfEditDocumentStylesLogic = ({openModal, setOpenModal, setConfig, config}: ModalConfigPdfEditDocumentStylesLogicProps) => {
    const { id } = useParams()
    const saveInfo = useCallback(async (vals: any) => {
        await addAntiUnsaveElement(['pdf_template_changes', '{}'])
        const allRowData = openModal?.allRowData
        if (allRowData) {
            let myImage = vals.backgroundImage
            if (myImage) {
                const myImageSplited = myImage.split('/')
                myImage = '/' + myImageSplited.splice(myImageSplited.length - 1, 1)
                const myUrlParsed = await twinFetchPostText('/api/app/fileSystem/getLinkOfFile', { type: 'pdfTemplate', subFolder: '/' + id, filepath: myImage })
                vals['backgroundImageParsed'] = myUrlParsed
            } else if (myImage === '') {
                vals['backgroundImageParsed'] = ''
                vals['backgroundImage'] = ''
            }
            setConfig((old) => {
                if (!old) {
                    return null
                }
                const copy = JSON.parse(JSON.stringify(old))
                copy[allRowData.id].cssConfig = { ...copy[allRowData.id].cssConfig, ...vals }
                return { ...copy }
            })
            setOpenModal(null)
        }
    }, [setConfig, setOpenModal, openModal, id])

    const prevSubmitEditModule: BeforeSubmitHandler = useCallback((vals) => {
        saveInfo(vals)
        return false
    }, [saveInfo])

    const getMyData = useCallback((key: string) => {
        let allRowData: any = {}
        if (key) {
            let myconfig: any = { ...config }
            let myKey = key
            allRowData = myconfig[myKey].cssConfig
        }
        return allRowData
    }, [config])
    
    return { prevSubmitEditModule, getMyData }
}

export default ModalConfigPdfEditDocumentStyles